import { Component, type ErrorInfo, type ReactNode } from "react";
import ErrorFallback from "@/components/ErrorFallback";
import { logClientError } from "@/utils/errorLogger";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  correlationId: string | null;
}

class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, correlationId: null };
  }

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { correlationId } = logClientError(error, {
      ...(errorInfo.componentStack != null && {
        componentStack: errorInfo.componentStack,
      }),
    });
    this.setState({ correlationId });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, correlationId: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          errorId={this.state.correlationId ?? undefined}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
